// TODO: SPT:REMOVE
import { useFeatureFlag } from 'configcat-react';
import { dashboardConstants } from '@/lib/constants';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useMapEvents } from 'react-leaflet';
import * as turf from '@turf/turf';
import {
  requestTeamCellsAsync,
  requestTeamPinsAsync,
  selectTeamCells,
} from '@/redux/teams';
import { sptConstants, doPolygonsIntersect, unionPolygons } from '../../lib';
import { PixiOverlay } from '../PixiOverlay';
import { LoaderControl, LassoControl } from '../controls';
import { HeatMap, WeeksSinceKnockedLayer } from './layers/HeatMap';
import { TeamMapContext } from '../../providers/TeamMapProvider';
import { DropdownButton } from '@/components';
import { Tab } from '@headlessui/react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

const {
  CELL_RESOLUTIONS_ENABLED,
  NATION_PIN_SCORE_FLAG_NAME,
  POLYGON_LASSO_SELECTION_FLAG_NAME,
} = dashboardConstants;

const {
  QUALIFIED_ADDRESSES,
  QUALIFIED_ADDRESSES_MAP_TYPE,
  WEEKS_SINCE_KNOCKED,
  WEEKS_SINCE_KNOCKED_MAP_TYPE,
  PIN_SCORE,
  PIN_SCORE_MAP_TYPE,
  NATIONAL_PIN_SCORE_MAP_TYPE,
  NONE,
  NONE_MAP_TYPE,
} = sptConstants;

const TeamChoropleth = ({
  loading,
  getTeamCells,
  getTeamPins,
  teamCells,
  teamId,
  selectedPolygonId,
  setViewBoundary,
  viewBoundary,
}) => {
  const { value: isCellResolutionsEnabled, loading: isCellResolutionsLoading } = useFeatureFlag(
    CELL_RESOLUTIONS_ENABLED,
    false,
  );
  const {
    value: isNationalPinScoreEnabled,
    loading: isNationalPinScoreLoading,
  } = useFeatureFlag(NATION_PIN_SCORE_FLAG_NAME, false);

  const { value: isPolygonLassoSelectionEnabled } = useFeatureFlag(
    POLYGON_LASSO_SELECTION_FLAG_NAME,
    false,
  );

  const {
    heatMap,
    setHeatMap,
    pinMode,
    setPinMode,
    displayPins,
    isPinsScoreMode,
    displayPinsOutcomes,
    drawMode,
    handleDrawModeChange,
  } = useContext(TeamMapContext);

  const [resolution, setResolution] = useState(6);
  const [pixiLoaded, setPixiLoaded] = useState(true);
  const [dropdownLabel, setDropdownLabel] = useState(QUALIFIED_ADDRESSES);
  const [displayAreaPinScore, toggleDisplayAreaPinScore] = useState(heatMap === PIN_SCORE_MAP_TYPE);

  const dropdownOptions = [
    {
      key: QUALIFIED_ADDRESSES_MAP_TYPE,
      label: QUALIFIED_ADDRESSES,
      onClick: () => {
        setDropdownLabel(QUALIFIED_ADDRESSES);
        setHeatMap(QUALIFIED_ADDRESSES_MAP_TYPE);
      },
    },
    {
      key: WEEKS_SINCE_KNOCKED_MAP_TYPE,
      label: WEEKS_SINCE_KNOCKED,
      onClick: () => {
        setDropdownLabel(WEEKS_SINCE_KNOCKED);
        setHeatMap(WEEKS_SINCE_KNOCKED_MAP_TYPE);
      },
    },
    {
      key: PIN_SCORE_MAP_TYPE,
      label: PIN_SCORE,
      onClick: () => {
        setDropdownLabel(PIN_SCORE);
        setHeatMap(displayAreaPinScore ? PIN_SCORE_MAP_TYPE : NATIONAL_PIN_SCORE_MAP_TYPE);
      },
    },
    {
      key: NONE_MAP_TYPE,
      label: NONE,
      onClick: () => {
        setDropdownLabel(NONE);
        setHeatMap(NONE_MAP_TYPE);
      },
    },
  ];

  const map = useMapEvents({
    dragend: () => {
      if (!map) {
        return;
      }

      if (!loading.teamCells) {
        handleMapChange();
      }
    },
    zoomend: () => {
      const zoom = map.getZoom();

      if (zoom >= 13) {
        setResolution(8);
      } else if (zoom > 10 && zoom < 13) {
        setResolution(7);
      } else if (zoom <= 10) {
        setResolution(6);
      }

      if (!map) {
        return;
      }

      if (!loading.teamCells) {
        handleMapChange();
      }
    },
  });

  const togglePinScoreType = useCallback(() => {
    const newDisplayAreaPinScore = !displayAreaPinScore;

    toggleDisplayAreaPinScore(newDisplayAreaPinScore);

    setHeatMap(newDisplayAreaPinScore ? PIN_SCORE_MAP_TYPE : NATIONAL_PIN_SCORE_MAP_TYPE);
  }, [displayAreaPinScore, setHeatMap]);

  const requestTeamCells = useCallback(() => {
    if (viewBoundary) {
      if (!isCellResolutionsLoading) {
        getTeamCells({
          team_id: teamId,
          resolution: isCellResolutionsEnabled ? resolution : 8,
          boundary: viewBoundary,
        });
      }
    }
  }, [teamId, getTeamCells, resolution, viewBoundary, isCellResolutionsLoading, isCellResolutionsEnabled]);

  const requestTeamPins = useCallback((pinRequestBoundary) => {
    getTeamPins({
      team_id: teamId,
      boundary: pinRequestBoundary,
    });
  }, [getTeamPins, teamId]);

  const getBoundsWindow = useCallback(() => {
    const bounds = map.getBounds();
    const northWest = bounds.getNorthWest();
    const northEast = bounds.getNorthEast();
    const southEast = bounds.getSouthEast();
    const southWest = bounds.getSouthWest();

    return [
      [
        [northEast.lng, northEast.lat],
        [southEast.lng, southEast.lat],
        [southWest.lng, southWest.lat],
        [northWest.lng, northWest.lat],
        [northEast.lng, northEast.lat],
      ],
    ];
  }, [map]);

  const handleMapChange = useCallback(() => {
    const viewBounds = turf.polygon(getBoundsWindow());
    setViewBoundary(getBoundsWindow());

    let totalAddresses = 0;
    const viewableCells = [];
    if (teamCells.length) {
      teamCells.forEach((thisCell) => {
        const intersects = doPolygonsIntersect(thisCell?.geometry, viewBounds);

        if (intersects) {
          viewableCells.push(thisCell);
          totalAddresses += thisCell?.properties?.qualified_addresses ?? 0;
        }
      });
    }

    if (totalAddresses <= 5000 && viewableCells.length) {
      setPinMode(true);
      const combinedCells = unionPolygons(viewableCells);
      const pinRequestBoundary = turf.intersect(viewBounds, combinedCells);
      requestTeamPins(pinRequestBoundary.geometry);
    } else {
      setPinMode(false);
    }
  }, [teamCells, getBoundsWindow, requestTeamPins, setPinMode, setViewBoundary]);

  useEffect(() => {
    requestTeamCells();
  }, [requestTeamCells]);

  useEffect(() => {
    setViewBoundary(getBoundsWindow());
    // This effect needs to be run only once on initial component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {(!pixiLoaded) && <LoaderControl />}

      <PixiOverlay
        setLoading={setPixiLoaded}
        isPinMode={pinMode && displayPins}
        heatMap={isPinsScoreMode ? heatMap : QUALIFIED_ADDRESSES_MAP_TYPE}
        isPinOutcomesMode={pinMode && displayPinsOutcomes}
        selectedPolygon={selectedPolygonId}
      />

      {!pinMode &&
        !loading.teamCells &&
        (
          <div>
            <div className="leaflet-top leaflet-right mr-12 !z-[1001]">
              <div className="flex gap-x-2 leaflet-control !cursor-pointer">
                {!isNationalPinScoreLoading &&
                  isNationalPinScoreEnabled &&
                  [PIN_SCORE_MAP_TYPE, NATIONAL_PIN_SCORE_MAP_TYPE].includes(heatMap) &&
                  (
                    <Tab.Group selectedIndex={displayAreaPinScore ? 1 : 0} onChange={() => togglePinScoreType()}>
                      <Tab.List className="flex justify-around px-2 mr-4 bg-white rounded-lg border-2 gap-x-2 text-gray-700 hover:outline-none hover:ring-2 hover:ring-offset-2 hover:ring-aptivegreen">
                        {[{ name: 'National Pin Score' }, { name: 'Area Pin Score' }].map(({ name: tabName }) => (
                          <Tab as={Fragment} key={uuidv4()}>
                            {({ selected }) => (
                              <button
                                type="button"
                                className={classNames(
                                  'border-b-2 pt-[2px] hover:opacity-100',
                                  selected
                                    ? 'border-aptivegreen opacity-100'
                                    : 'border-transparent opacity-50',
                                )}
                              >
                                {tabName}
                              </button>
                            )}
                          </Tab>
                        ))}
                      </Tab.List>
                    </Tab.Group>
                  )}
                <DropdownButton
                  classNames="rounded-lg border-2 border-black/25"
                  label={dropdownLabel}
                  color="white"
                  options={dropdownOptions}
                />
                {isPolygonLassoSelectionEnabled &&
                  <LassoControl drawMode={drawMode} changeDrawMode={handleDrawModeChange} />
                }
              </div>
            </div>
            {heatMap === QUALIFIED_ADDRESSES_MAP_TYPE && <HeatMap mapType={QUALIFIED_ADDRESSES_MAP_TYPE} />}
            {heatMap === WEEKS_SINCE_KNOCKED_MAP_TYPE && <WeeksSinceKnockedLayer />}
            {[PIN_SCORE_MAP_TYPE, NATIONAL_PIN_SCORE_MAP_TYPE].includes(heatMap) && <HeatMap mapType={heatMap} />}
          </div>
        )
      }
    </div>
  );
};

const mapStateToProps = (state) => {
  const { loading, teams, errors } = state;

  return {
    loading: {
      teamCells: Boolean(loading?.teamCells?.isLoading) || !teams.teamCells,
      teamPins: Boolean(loading?.teamPins?.isLoading) || !teams.teamPins,
    },
    teamCells: selectTeamCells(state),
    errors: errors.errors,
  };
};

const mapDispatchToProps = {
  getTeamCells: requestTeamCellsAsync.request,
  getTeamPins: requestTeamPinsAsync.request,
};

TeamChoropleth.propTypes = {
  loading: PropTypes.object,
  getTeamCells: PropTypes.func,
  teamCells: PropTypes.array,
  getTeamPins: PropTypes.func,
  teamPins: PropTypes.array,
  teamId: PropTypes.string,
  pinMode: PropTypes.bool,
  setPinMode: PropTypes.func,
  displayPins: PropTypes.bool,
  selectedPolygonId: PropTypes.object,
  setViewBoundary: PropTypes.func,
  viewBoundary: PropTypes.array,
};

export default connect(mapStateToProps, mapDispatchToProps)(TeamChoropleth);
